import React from "react";
import StoreName from "./StoreName";
import CompanyName from "./CompanyName";
import Notifications from "./Notifications";
import Account from "./Account";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import useSupportFeedbackStore from "../../../../stores/supportFeedbackStore";

// Shared page header for the Operations section (Tasks, Logbook, Tickets,
// Planning Board, Headcount Planning, Contests): eyebrow + title on the
// left; on the right either the store dropdown + notifications + account
// (scope="store") or the company dropdown + account (scope="company";
// notifications are per-store so they are dropped there). `actions` render
// left of the selector; `children` (usually SubTabs) render under the row;
// `betaSupportContext` adds the Beta pill (opens the support modal);
// `eligibleCompanyIds` limits the company dropdown to those companies;
// `onBack` puts a back arrow before the title for a drill-in page (pass
// `eyebrow={null}` there so the arrow lines up with the title), and
// `titleBadge` renders a node (a status pill) beside the title.
export default function OperationsPageHeader({
  title,
  eyebrow = "Operations",
  onBack = null,
  titleBadge = null,
  scope = "store",
  homeStore,
  homeCompany,
  storeOptions,
  storeNames,
  change_home_store,
  actions = null,
  betaSupportContext = null,
  eligibleCompanyIds = null,
  children,
}) {
  const { openSupportModal } = useSupportFeedbackStore();
  return (
    <div className="mx-10">
      <div className="flex items-start justify-between gap-4 sm:flex-col sm:items-stretch">
        <div className="flex min-w-0 flex-1 items-center gap-2">
          {onBack && (
            <button
              type="button"
              onClick={onBack}
              aria-label="Back"
              className="flex h-9 w-9 items-center justify-center rounded-full text-slate-700 hover:bg-slate-100"
            >
              <ArrowBackIcon />
            </button>
          )}
          <div className="min-w-0">
            {eyebrow && (
              <div className="text-[11px] font-bold uppercase tracking-wider text-slate-400">
                {eyebrow}
              </div>
            )}
            <div className="flex flex-wrap items-center gap-3">
              <h1 className="min-w-0 break-words text-2xl font-bold text-slate-900 tracking-tight mt-0.5">
                {title}
              </h1>
              {titleBadge}
              {betaSupportContext && (
                <button
                  type="button"
                  onClick={() => openSupportModal(betaSupportContext)}
                  className="px-3 py-1 text-xs font-semibold uppercase tracking-wide rounded-full border border-violet-200 text-violet-700 bg-violet-50 hover:bg-violet-100"
                >
                  Beta
                </button>
              )}
            </div>
          </div>
        </div>
        <div className="flex min-w-0 flex-wrap items-center justify-end gap-y-2">
          {actions && (
            <div className="mr-6 flex flex-wrap items-center gap-2">{actions}</div>
          )}
          <div className={`min-w-0 ${scope === "store" ? "mr-6" : "mr-8"}`}>
            {scope === "store" ? (
              <StoreName
                storeOptions={storeOptions}
                homeStore={homeStore}
                storeNames={storeNames}
                change_home_store={change_home_store}
              />
            ) : (
              <CompanyName
                homeStore={homeStore}
                homeCompany={homeCompany}
                storeOptions={storeOptions}
                storeNames={storeNames}
                eligibleCompanyIds={eligibleCompanyIds}
                change_home_store={change_home_store}
              />
            )}
          </div>
          {scope === "store" && (
            <div className="mr-8">
              <Notifications homeStore={homeStore} />
            </div>
          )}
          <div className="h-10 flex items-center justify-center cursor-pointer">
            <Account />
          </div>
        </div>
      </div>
      {children}
    </div>
  );
}
